import { ApiError, ApiErrorCode } from './apiError';
import { endpoint } from './endpoint';
import {
  type AdminCoreUploadsCompleteData,
  type AdminCoreUploadsCreateData,
  type FileOut,
  type UploadCreateIn,
  type UploadTicketOut,
  zAdminCoreUploadsCompleteResponse,
  zAdminCoreUploadsCreateResponse,
} from './generated';
import { http, toApiError } from './http';
import { parseApiResponse } from './parse';

/**
 * Загрузка файлов админки (COM-13…14, docs/decisions.md «Загрузки»):
 * `POST /admin/uploads {purpose, content_type, size}` → presigned URL (15 минут) →
 * `PUT` файла на этот URL с выданными заголовками → `POST /admin/uploads/{upload_id}/complete`
 * (сервер проверяет размер и тип по содержимому). `upload_id` — это `file_id` для сущностей.
 */
export const UploadPurpose = {
  /** Изображения контента: JPG, PNG, WebP, HEIC — до 10 МБ. */
  Image: 'image',
  /** Документы: PDF — до 25 МБ. */
  Document: 'document',
  /** Фото профиля автора (ACC 4.7.4): квадратное JPG/PNG от 400 px, до 10 МБ. */
  AuthorPhoto: 'author_photo',
  /** Дополнительные фото автора: JPG, PNG, WebP, HEIC — до 10 МБ. */
  AuthorGallery: 'author_gallery',
  /** Фото на весь экран приветствия онбординга: JPG/PNG, вертикальное 3:4, до 10 МБ. */
  OnboardingBackground: 'onboarding_background',
  /** Значок карточки или цели онбординга: SVG до 512 КБ. */
  OnboardingIcon: 'onboarding_icon',
  /**
   * Изображения GEO — стран, регионов, виноделен (BR-GEO-59): JPG, PNG, WebP, HEIC — до 25 МБ;
   * правила места (обложка от 1600 px, 3:2, 1:1, PNG штампов) проверяет сервис медиа GEO.
   */
  GeoImage: 'geo_image',
  /**
   * Фото вина, сета и дропа (ТЗ CAT 6.3.6): JPG или PNG до 25 МБ (`modules/cat/setup.py`); правила
   * обложки сета и дропа (от 1600 px по длинной стороне) проверяет сервер.
   */
  CatImage: 'cat_image',
  /**
   * Обложка и фото дегустаций, экскурсий и поездок (BR-EXP-09): JPG или PNG до 25 МБ
   * (`modules/exp/setup.py`).
   */
  ExpImage: 'exp_image',
  /**
   * Медиа курсов, уроков и тестов (обложка, галерея, «Картинки и иконки»): изображения до 25 МБ
   * (`modules/lrn/setup.py`).
   */
  LrnImage: 'lrn_image',
  /**
   * Значок уровня XP, ступени паспорта и награды (ТЗ GAM 11.7): PNG или SVG до 1 МБ, только
   * администратор (`modules/gam/setup.py`).
   */
  GamIcon: 'gam_icon',
  /** Значок уровня точности и медальон специализации (ТЗ TST 10.7.3–10.7.4): PNG или SVG до 1 МБ. */
  TstBadge: 'tst_badge',
  /** Фото публикации (ТЗ CNT 12.3.2): JPG, PNG или HEIC до 15 МБ (`modules/cnt/setup.py`). */
  CntPhoto: 'cnt_photo',
  /** Файл документа (ТЗ CNT 12.3.10): PDF, DOCX, JPG или PNG до 20 МБ. */
  CntDocument: 'cnt_document',
  /** Логотип платформы (хендофф §9): SVG или PNG до 2 МБ, только администратор. */
  BrandLogo: 'brand_logo',
  /**
   * Картинка рассылки конструктора (ТЗ SRV 13.7.3.3): JPG или PNG до 5 МБ, только администратор
   * (`modules/srv/setup.py`); в пуше — справа от текста, в письме — над текстом.
   */
  CampaignImage: 'campaign_image',
} as const;
export type UploadPurpose = (typeof UploadPurpose)[keyof typeof UploadPurpose];

/**
 * Коды ошибок загрузки: ядра бэкенда (COM-14: 422 — тип и размер, 409 — состояние загрузки) и
 * клиентский `storage_rejected` — хранилище отклонило PUT без конверта ошибки API (например, S3
 * ответил XML 403 на устаревшую подпись).
 */
export const UploadErrorCode = {
  FileTypeNotAllowed: 'file_type_not_allowed',
  FileTooLarge: 'file_too_large',
  FileNotUploaded: 'file_not_uploaded',
  UploadExpired: 'upload_expired',
  StorageRejected: 'storage_rejected',
} as const;
export type UploadErrorCode = (typeof UploadErrorCode)[keyof typeof UploadErrorCode];

const MB = 1024 * 1024;

/** Набор форматов для подписей и ошибок типа: `form:upload.formats.<kind>`. */
export type UploadFormats = 'image' | 'photo' | 'pdf' | 'svg' | 'pngSvg' | 'photoHeic' | 'document';

export interface UploadRule {
  /** Допустимые MIME-типы (как их проверяет сервер). */
  contentTypes: readonly string[];
  /** Подпись форматов («JPG, PNG, WebP или HEIC»). */
  formats: UploadFormats;
  /** Расширения — для `accept` и для файлов, у которых браузер не определил тип (HEIC). */
  extensions: readonly string[];
  /** Предельный размер, байты. */
  maxSize: number;
}

/**
 * Ограничения назначений админки — те же, что у сервера (описание `POST /admin/uploads` в контракте,
 * COM-14). Клиент проверяет файл до загрузки, чтобы не тратить запрос; решает всё равно сервер.
 */
const IMAGE_RULE: UploadRule = {
  contentTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
  formats: 'image',
  extensions: ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'],
  maxSize: 10 * MB,
};
const PHOTO_RULE: UploadRule = {
  contentTypes: ['image/jpeg', 'image/png'],
  formats: 'photo',
  extensions: ['.jpg', '.jpeg', '.png'],
  maxSize: 10 * MB,
};

/** Значки GAM и TST: PNG или SVG на прозрачном фоне до 1 МБ. */
const PNG_SVG_RULE: UploadRule = {
  contentTypes: ['image/png', 'image/svg+xml'],
  formats: 'pngSvg',
  extensions: ['.png', '.svg'],
  maxSize: 1 * MB,
};
const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const UPLOAD_RULES: Readonly<Record<UploadPurpose, UploadRule>> = {
  [UploadPurpose.Image]: IMAGE_RULE,
  [UploadPurpose.Document]: {
    contentTypes: ['application/pdf'],
    formats: 'pdf',
    extensions: ['.pdf'],
    maxSize: 25 * MB,
  },
  [UploadPurpose.AuthorPhoto]: PHOTO_RULE,
  [UploadPurpose.AuthorGallery]: IMAGE_RULE,
  [UploadPurpose.OnboardingBackground]: PHOTO_RULE,
  [UploadPurpose.OnboardingIcon]: {
    contentTypes: ['image/svg+xml'],
    formats: 'svg',
    extensions: ['.svg'],
    maxSize: 512 * 1024,
  },
  [UploadPurpose.GeoImage]: { ...IMAGE_RULE, maxSize: 25 * MB },
  [UploadPurpose.CatImage]: { ...PHOTO_RULE, maxSize: 25 * MB },
  [UploadPurpose.ExpImage]: { ...PHOTO_RULE, maxSize: 25 * MB },
  [UploadPurpose.LrnImage]: { ...IMAGE_RULE, maxSize: 25 * MB },
  [UploadPurpose.GamIcon]: PNG_SVG_RULE,
  [UploadPurpose.TstBadge]: PNG_SVG_RULE,
  [UploadPurpose.CntPhoto]: {
    contentTypes: ['image/jpeg', 'image/png', 'image/heic', 'image/heif'],
    formats: 'photoHeic',
    extensions: ['.jpg', '.jpeg', '.png', '.heic', '.heif'],
    maxSize: 15 * MB,
  },
  [UploadPurpose.CntDocument]: {
    contentTypes: ['application/pdf', DOCX_TYPE, 'image/jpeg', 'image/png'],
    formats: 'document',
    extensions: ['.pdf', '.docx', '.jpg', '.jpeg', '.png'],
    maxSize: 20 * MB,
  },
  [UploadPurpose.BrandLogo]: { ...PNG_SVG_RULE, maxSize: 2 * MB },
  [UploadPurpose.CampaignImage]: { ...PHOTO_RULE, maxSize: 5 * MB },
};

/** Ссылка presigned живёт 15 минут (COM-13) — дольше ждать загрузку бессмысленно. */
const PRESIGNED_TTL_MS = 15 * 60_000;

const TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  '.svg': 'image/svg+xml',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.pdf': 'application/pdf',
  '.docx': DOCX_TYPE,
};

const TYPE_ALIASES: Readonly<Record<string, string>> = {
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'image/x-png': 'image/png',
};

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.slice(dot).toLowerCase() : '';
}

/** MIME-тип файла: из браузера, а если он пустой (HEIC в части браузеров) — по расширению. */
export function resolveContentType(file: Pick<File, 'name' | 'type'>): string {
  const declared = file.type.split(';', 1)[0]?.trim().toLowerCase() ?? '';
  if (declared) return TYPE_ALIASES[declared] ?? declared;
  return TYPE_BY_EXTENSION[extensionOf(file.name)] ?? 'application/octet-stream';
}

/** Атрибут `accept` для `<input type="file">`. */
export function acceptAttribute(purpose: UploadPurpose): string {
  const rule = UPLOAD_RULES[purpose];
  return [...rule.contentTypes, ...rule.extensions].join(',');
}

export type UploadValidationError =
  | { code: typeof UploadErrorCode.FileTypeNotAllowed; formats: UploadFormats }
  | { code: typeof UploadErrorCode.FileTooLarge; maxSize: number };

/** Проверка до загрузки по тем же правилам, что у сервера; null — файл подходит. */
export function validateUploadFile(
  file: Pick<File, 'name' | 'type' | 'size'>,
  purpose: UploadPurpose,
): UploadValidationError | null {
  const rule = UPLOAD_RULES[purpose];
  if (!rule.contentTypes.includes(resolveContentType(file))) {
    return { code: UploadErrorCode.FileTypeNotAllowed, formats: rule.formats };
  }
  if (file.size <= 0 || file.size > rule.maxSize) {
    return { code: UploadErrorCode.FileTooLarge, maxSize: rule.maxSize };
  }
  return null;
}

const CREATE = endpoint<AdminCoreUploadsCreateData>('/api/v1/admin/uploads');

/** Шаг 1: ссылка для загрузки. 422 `file_type_not_allowed` / `file_too_large` (поля content_type, size). */
export async function createUpload(
  body: UploadCreateIn,
  signal?: AbortSignal,
): Promise<UploadTicketOut> {
  const response = await http.post<unknown>(CREATE, body, { signal });
  return parseApiResponse(zAdminCoreUploadsCreateResponse, response.data, CREATE);
}

export interface PutFileOptions {
  /** Доля загруженного, 0…1. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * Шаг 2: файл — на presigned URL методом и с заголовками из ответа сервера. Тот же axios-инстанс
 * в режиме `external`: без baseURL и без заголовков авторизации (см. http.ts).
 */
export async function putFile(
  ticket: Pick<UploadTicketOut, 'url' | 'method' | 'headers'>,
  file: Blob,
  { onProgress, signal }: PutFileOptions = {},
): Promise<void> {
  try {
    await http.request({
      url: ticket.url,
      method: ticket.method,
      headers: { ...ticket.headers },
      data: file,
      external: true,
      signal,
      timeout: PRESIGNED_TTL_MS,
      onUploadProgress: onProgress
        ? (event) => {
            const total = event.total ?? file.size;
            if (total > 0) onProgress(Math.min(1, event.loaded / total));
          }
        : undefined,
    });
  } catch (error) {
    throw storageError(toApiError(error));
  }
}

/** Коды, которые хранилище (dev-адаптер бэкенда) отдаёт в конверте API, и сетевые/отмена. */
const STORAGE_KNOWN_CODES: ReadonlySet<string> = new Set([
  UploadErrorCode.FileTypeNotAllowed,
  UploadErrorCode.FileTooLarge,
  UploadErrorCode.UploadExpired,
  ApiErrorCode.NetworkError,
  ApiErrorCode.Aborted,
]);

function storageError(error: ApiError): ApiError {
  if (STORAGE_KNOWN_CODES.has(error.code)) return error;
  return new ApiError({
    code: UploadErrorCode.StorageRejected,
    message: error.message,
    status: error.status,
    requestId: error.requestId,
  });
}

/** Шаг 3: проверка загруженного файла. 409 `file_not_uploaded` / `upload_expired`, 422 тип/размер. */
export async function completeUpload(uploadId: string, signal?: AbortSignal): Promise<FileOut> {
  const url = endpoint<AdminCoreUploadsCompleteData>('/api/v1/admin/uploads/{upload_id}/complete', {
    upload_id: uploadId,
  });
  const response = await http.post<unknown>(url, undefined, { signal });
  return parseApiResponse(zAdminCoreUploadsCompleteResponse, response.data, url);
}

export type UploadFileOptions = PutFileOptions;

/** Полный цикл загрузки файла; результат — проверенный файл (`upload_id` = file_id сущности). */
export async function uploadFile(
  file: File,
  purpose: UploadPurpose,
  options: UploadFileOptions = {},
): Promise<FileOut> {
  const ticket = await createUpload(
    { purpose, content_type: resolveContentType(file), size: file.size },
    options.signal,
  );
  await putFile(ticket, file, options);
  options.onProgress?.(1);
  return completeUpload(ticket.upload_id, options.signal);
}
